import { Alert, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { CustomView } from '../../core/types/data-models';

interface NewCustomViewDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * LOB id the new view attaches to. Driven by the active LOB selected in
   * the sidebar — no UI picker; the dialog refuses to submit when null.
   */
  defaultLineOfBizId?: number | null;
  /** Called with the newly created view after a successful POST. */
  onCreated?: (view: CustomView) => void;
}

/**
 * Lightweight create dialog used by the sidebar's "+ New custom view"
 * entry under the Custom View dropdown.
 */
export function NewCustomViewDialog({
  open,
  onClose,
  defaultLineOfBizId = null,
  onCreated,
}: NewCustomViewDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setDescription('');
    setError(null);
  };

  const create = useMutation({
    mutationFn: (payload: Partial<CustomView>) => resources.customViews.create(payload),
    onSuccess: (view) => {
      queryClient.invalidateQueries({ queryKey: ['custom-views'] });
      onCreated?.(view);
      reset();
      onClose();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (defaultLineOfBizId == null) {
      setError('Select a line of business in the sidebar first.');
      return;
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        lineOfBizId: defaultLineOfBizId,
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <FormDialog
      open={open}
      title="New custom view"
      submitLabel="Create"
      loading={create.isPending}
      onClose={() => {
        reset();
        onClose();
      }}
      onSubmit={handleSubmit}
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Stack spacing={2}>
        <TextField
          label="Name"
          required
          autoFocus
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Description"
          multiline
          minRows={2}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Stack>
    </FormDialog>
  );
}
